import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { keys } from '../../api/keys'
import type { ReportingCurrency } from '../../api/reportingCurrencies'
import { loadProfile } from '../../auth/session'
import { emit } from '../../platform/analytics/analytics'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'

type Profile = components['schemas']['UserResponse']

export type ReportingCurrencyState = { chosen: string | null; effective: string | null }

/** The read side, straight off the current-user query (a shared profile hook can replace the query once one exists). */
export function useReportingCurrencyState(): AsyncState<ReportingCurrencyState> {
  const query = useQuery({
    queryKey: keys.user.me(),
    queryFn: loadProfile,
    select: (p: Profile): ReportingCurrencyState => ({
      chosen: p.reporting_currency ?? null,
      effective: p.effective_reporting_currency ?? null,
    }),
  })
  return fromQuery(query, {
    message: () => 'We couldn’t load your currency. Check your connection and try again.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * `PATCH /users/me` with `reporting_currency` and NOTHING else. `null` is sent
 * explicitly for Automatic: the server tells an explicit null (clear) from an
 * omitted key (no-op), and JSON drops `undefined`.
 *
 * Optimistic on the user query, rolled back on failure. `networkMode: 'always'`
 * (mutations do not retry by default), so an offline save fails at once instead of pausing forever on
 * "saving". On settle it invalidates everything the server re-converts.
 */
export function useSetReportingCurrency() {
  const client = useQueryClient()
  return useMutation<void, Error, ReportingCurrency | null, { previous: Profile | undefined }>({
    networkMode: 'always',
    mutationFn: async (next) => {
      const { error } = await api.PATCH('/api/v1/users/me', { body: { reporting_currency: next } })
      if (error !== undefined) throw new Error('Could not save the currency.')
    },
    onMutate: async (next) => {
      await client.cancelQueries({ queryKey: keys.user.me() })
      const previous = client.getQueryData<Profile>(keys.user.me())
      if (previous) client.setQueryData<Profile>(keys.user.me(), { ...previous, reporting_currency: next })
      return { previous }
    },
    onError: (_error, _next, context) => {
      // Restore only this field: a whole-snapshot restore would drop another
      // in-flight write to the same cache entry (learn_from_edits, display name).
      if (context?.previous)
        client.setQueryData<Profile>(keys.user.me(), (cur) =>
          cur && { ...cur, reporting_currency: context.previous?.reporting_currency },
        )
    },
    onSuccess: (_data, next) => {
      emit('currency_changed', next === null ? { mode: 'automatic' } : { mode: 'chosen', currency: next })
    },
    onSettled: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.user.me() }),
        client.invalidateQueries({ queryKey: keys.cashflow.all() }),
        client.invalidateQueries({ queryKey: keys.insights.all() }),
      ]),
  })
}
